import { App, Descriptions, Divider, Dropdown, Empty, Form, Input, Modal, Select, Spin, Tag, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { subscribeAttendanceMonthEntries, upsertAttendanceEntry } from '@services/registry';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';

const { Text } = Typography;
const { TextArea } = Input;

const DAY_W = 48;
const MEMBER_W = 280;
const TOTAL_W = 75;

const TableShell = styled.div`
    width: 100%;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#303030' : '#e2e8f0')};
    border-radius: 14px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#141414' : '#ffffff')};
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    min-height: calc(100vh - 240px);
    display: flex;
    flex-direction: column;
`;

const ScrollX = styled.div`
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x pan-y;
    flex: 1;
    scrollbar-width: thin;
    
    &::-webkit-scrollbar {
        height: 8px;
    }
    &::-webkit-scrollbar-track {
        background: #f1f5f9;
    }
    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
    }
`;

const HeaderGrid = styled.div`
    display: grid;
    grid-template-columns: ${MEMBER_W}px repeat(${({ $days }) => $days}, ${DAY_W}px) ${TOTAL_W}px;
    min-width: ${({ $minWidth }) => $minWidth}px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#e2e8f0')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#f8fafc')};
`;

const RowGrid = styled.div`
    display: grid;
    grid-template-columns: ${MEMBER_W}px repeat(${({ $days }) => $days}, ${DAY_W}px) ${TOTAL_W}px;
    min-width: ${({ $minWidth }) => $minWidth}px;

    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f1f5f9')};
    }

    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1c1c1c' : '#f8fafc')};
    }
`;

const HeaderCell = styled.div`
    padding: 10px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f1f5f9')};
    min-height: 48px;
    background: ${({ $isToday }) => ($isToday ? '#ecfdf5' : 'transparent')};
`;

const StickyHeaderMember = styled(HeaderCell)`
    position: sticky;
    left: 0;
    z-index: 5;
    justify-content: flex-start;
    padding-left: 16px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#f8fafc')};
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.04);
`;

const StickyHeaderTotal = styled(HeaderCell)`
    position: sticky;
    right: 0;
    z-index: 5;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#f8fafc')};
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.04);
`;

const StickyMemberCell = styled.div`
    position: sticky;
    left: 0;
    z-index: 4;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f1f5f9')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#141414' : '#ffffff')};
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.04);
`;

const StickyTotalCell = styled.div`
    position: sticky;
    right: 0;
    z-index: 4;
    padding: 10px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f1f5f9')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#141414' : '#ffffff')};
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.04);
    font-weight: 800;
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f1f5f9' : '#0f172a')};
`;

const Cell = styled.div`
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f1f5f9')};
    min-height: 56px;
    background: ${({ $isPresent, $isToday }) => {
        if ($isPresent) return '#fffbeb'; // Present light yellow/gold background highlight
        if ($isToday) return '#f0fdf4'; // Light green current day column shade
        return 'transparent';
    }};
    transition: background-color 0.15s ease;
`;

const MemberWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    width: 100%;
`;

const AvatarCircle = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${({ $bg }) => $bg || '#0284c7'};
    color: #ffffff;
    font-weight: 800;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    text-transform: uppercase;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const MemberMeta = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

const MemberName = styled.div`
    font-weight: 700;
    font-size: 13px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f1f5f9' : '#1e293b')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MemberEmail = styled.div`
    font-size: 11px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#94a3b8' : '#64748b')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DayHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.2;
    gap: 2px;
`;

const DayNum = styled.div`
    font-weight: 800;
    font-size: 12px;
    color: ${({ $isToday, theme }) => {
        if ($isToday) return '#059669';
        return theme.colors.background.default === '#141414' ? '#e2e8f0' : '#334155';
    }};
`;

const DayName = styled.div`
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    color: ${({ $isWeekend, $isToday }) => {
        if ($isWeekend) return '#ef4444'; // Red font for Saturday and Sunday
        if ($isToday) return '#059669';
        return '#64748b';
    }};
`;

const MarkBtn = styled.button`
    border: none;
    background: transparent;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 14px;
    font-family: inherit;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    color: ${({ $status, theme }) => {
        if ($status === 'P') return '#b45309'; // Gold / amber font for Present
        if ($status === 'A') return '#ef4444'; // Bright red for Absent
        if ($status === 'H') return '#ef4444'; // Bright red for Holiday
        if ($status === 'LV') return '#d97706';
        if ($status === 'EV') return '#2563eb';
        return theme.colors.background.default === '#141414' ? '#475569' : '#94a3b8';
    }};
    
    &:hover {
        opacity: ${({ disabled }) => (disabled ? 1 : 0.8)};
        transform: ${({ disabled }) => (disabled ? 'none' : 'scale(1.15)')};
    }
    transition: transform 0.1s ease, opacity 0.1s ease;
`;

const AVATAR_COLORS = [
    '#e11d48', // Red
    '#8b5cf6', // Purple
    '#059669', // Emerald
    '#0284c7', // Cyan/Blue
    '#d97706', // Amber
    '#7c3aed', // Violet
    '#db2777', // Pink
    '#0891b2', // Teal
    '#4f46e5', // Indigo
    '#ca8a04', // Gold
];

function getAvatarBg(index, name = '') {
    if (!name) return AVATAR_COLORS[index % AVATAR_COLORS.length];
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitial(name = '') {
    const trimmed = name.trim();
    if (!trimmed) return 'T';
    return trimmed[0].toUpperCase();
}

function weekdayKey(date) {
    const d = dayjs(date);
    const dow = d.day(); // 0 sun
    if (dow === 0) return 'sun';
    if (dow === 1) return 'mon';
    if (dow === 2) return 'tue';
    if (dow === 3) return 'wed';
    if (dow === 4) return 'thu';
    if (dow === 5) return 'fri';
    return 'sat';
}

function isOffDay({ attendanceConfig, date }) {
    const d = dayjs(date);
    const dow = d.day(); // 0 = Sun, 6 = Sat
    
    const b0 = attendanceConfig?.branches?.[0];
    if (b0) {
        const effectiveFrom = b0?.effectiveFrom ? dayjs(b0.effectiveFrom, 'YYYY-MM-DD', true) : null;
        if (effectiveFrom?.isValid?.() && d.isBefore(effectiveFrom, 'day')) {
            return dow === 0 || dow === 6;
        }
        const k = weekdayKey(date);
        const cur = b0?.schedule?.[k];
        if (cur && typeof cur.on === 'boolean') {
            return cur.on === false;
        }
    }
    // Default Saturday and Sunday to Holiday if no branch schedule is configured
    return dow === 0 || dow === 6;
}

function startTimeForDate({ attendanceConfig, date }) {
    const b0 = attendanceConfig?.branches?.[0];
    const k = weekdayKey(date);
    const cur = b0?.schedule?.[k];
    return cur?.from || null;
}

function to12h(valueHHmm) {
    if (!valueHHmm) return null;
    const d = dayjs(valueHHmm, 'HH:mm', true);
    return d.isValid() ? d.format('hh:mm A') : null;
}

function calcDefaultStatus({ date, now, hasEntry }) {
    const d = dayjs(date);
    const today = dayjs(now).startOf('day');
    if (d.isAfter(today, 'day')) return { value: '--', disabled: true };
    // By default, unmarked active workdays display 'A' (Absent)
    return { value: hasEntry ? null : 'A', disabled: false };
}

function statusLabel(s) {
    if (s === 'P') return 'Present';
    if (s === 'A') return 'Absent';
    if (s === 'LV') return 'Leave';
    if (s === 'EV') return 'Event';
    if (s === 'H') return 'Holiday';
    if (s === '--') return 'Future Date';
    if (!s) return 'Not marked';
    return String(s);
}

export default function AttendanceGrid({
    tenantId,
    scope,
    monthKey,
    attendanceConfig,
    rows,
    loading,
    getRowId,
    getRowName,
    getRowEmail,
    onError,
    onStatusSaved,
}) {
    const { message } = App.useApp();
    const { dateFormat, dateTimeFormat } = useTenantDateFormat();
    const [entries, setEntries] = useState([]);
    const [savingKey, setSavingKey] = useState(null);
    const [prompt, setPrompt] = useState(null);
    const [promptOpen, setPromptOpen] = useState(false);
    const [promptForm] = Form.useForm();
    const [context, setContext] = useState(null);
    const [viewOpen, setViewOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    useEffect(() => {
        if (!tenantId || !scope || !monthKey) return;
        return subscribeAttendanceMonthEntries({
            tenantId,
            scope,
            monthKey,
            onNext: setEntries,
            onError,
        });
    }, [tenantId, scope, monthKey, onError]);

    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60 * 1000);
        return () => clearInterval(t);
    }, []);

    const start = useMemo(() => dayjs(`${monthKey}-01`), [monthKey]);
    const daysInMonth = start.daysInMonth();
    const minWidth = MEMBER_W + daysInMonth * DAY_W + TOTAL_W;

    const entryMap = useMemo(() => {
        const map = new Map();
        for (const e of entries) {
            if (!e?.dateKey || !e?.personId) continue;
            map.set(`${e.dateKey}_${e.personId}`, e);
        }
        return map;
    }, [entries]);

    const dayCols = useMemo(() => {
        return Array.from({ length: daysInMonth }).map((_, i) => {
            const d = start.date(i + 1);
            return {
                dateKey: d.format('YYYY-MM-DD'),
                labelNum: String(i + 1).padStart(2, '0'),
                labelDow: d.format('ddd'),
                date: d.toDate(),
                isWeekend: d.day() === 0 || d.day() === 6,
                isToday: d.isSame(dayjs(now), 'day'),
            };
        });
    }, [daysInMonth, start, now]);

    const safeRows = Array.isArray(rows) ? rows : [];

    const nameById = useMemo(() => {
        const map = new Map();
        for (const r of safeRows) {
            const id = getRowId?.(r);
            if (!id) continue;
            map.set(id, getRowName?.(r) || '—');
        }
        return map;
    }, [getRowId, getRowName, safeRows]);

    const rowById = useMemo(() => {
        const map = new Map();
        for (const r of safeRows) {
            const id = getRowId?.(r);
            if (!id) continue;
            map.set(id, r);
        }
        return map;
    }, [getRowId, safeRows]);

    const setStatus = async ({ personId, dateKey, status, checkInTime, reason }) => {
        if (!tenantId) return;
        const key = `${dateKey}_${personId}`;
        setSavingKey(key);
        try {
            await upsertAttendanceEntry({ tenantId, scope, monthKey, dateKey, personId, status, checkInTime, reason });
            try {
                await onStatusSaved?.({
                    scope,
                    monthKey,
                    personId,
                    dateKey,
                    status,
                    checkInTime: checkInTime || null,
                    reason: reason || null,
                    row: rowById.get(personId) || null,
                });
            } catch {
                // Notification failure soft catch
            }
        } catch (e) {
            onError?.(e);
        } finally {
            setSavingKey(null);
        }
    };

    const handleCellClick = (personId, dateKey, date, currentShown) => {
        const key = `${dateKey}_${personId}`;
        if (savingKey === key) return;

        // Toggle P <-> A on single click for workdays
        const nextStatus = currentShown === 'P' ? 'A' : 'P';
        void setStatus({ personId, dateKey, status: nextStatus });
    };

    const openPrompt = ({ personId, dateKey, date }) => {
        const d = dayjs(date);
        const today = dayjs(now).startOf('day');
        const mode = d.isSame(today, 'day') ? 'today' : d.isBefore(today, 'day') ? 'past' : 'future';
        if (mode === 'future') return;
        const holiday = isOffDay({ attendanceConfig, date });

        const startHHmm = startTimeForDate({ attendanceConfig, date });
        const start12 = to12h(startHHmm);
        setPrompt({ personId, dateKey, date, mode, startHHmm, start12, holiday });
        promptForm.resetFields();
        if (holiday) {
            promptForm.setFieldsValue({ holidayAction: 'P' });
        } else if (mode === 'today') {
            promptForm.setFieldsValue({ todayStatus: 'P' });
        } else {
            promptForm.setFieldsValue({ pastStatus: 'P' });
        }
        setPromptOpen(true);
    };

    const submitPrompt = async () => {
        if (!prompt?.personId || !prompt?.dateKey) return;
        try {
            const v = await promptForm.validateFields();
            const key = `${prompt.dateKey}_${prompt.personId}`;
            if (savingKey === key) return;

            if (prompt.holiday) {
                const action = v.holidayAction;
                if (action === 'EV') {
                    const eventName = (v.eventName || '').trim();
                    if (!eventName) {
                        message.error('Event name is required');
                        return;
                    }
                    await setStatus({
                        personId: prompt.personId,
                        dateKey: prompt.dateKey,
                        status: 'EV',
                        reason: eventName,
                    });
                    setPromptOpen(false);
                    return;
                }
                await setStatus({
                    personId: prompt.personId,
                    dateKey: prompt.dateKey,
                    status: 'P',
                });
                setPromptOpen(false);
                return;
            }

            if (prompt.mode === 'today') {
                const status = v.todayStatus;
                if (status === 'LV') {
                    const reason = (v.reason || '').trim();
                    if (!reason) {
                        message.error('Leave reason is required');
                        return;
                    }
                    await setStatus({
                        personId: prompt.personId,
                        dateKey: prompt.dateKey,
                        status: 'LV',
                        reason,
                    });
                    setPromptOpen(false);
                    return;
                }

                const checkIn = v.todayCheckIn;
                const checkIn12 = checkIn ? checkIn.format('hh:mm A') : null;
                await setStatus({
                    personId: prompt.personId,
                    dateKey: prompt.dateKey,
                    status: 'P',
                    checkInTime: checkIn12,
                });
                setPromptOpen(false);
                return;
            }

            const status = v.pastStatus;
            if (status === 'LV') {
                const reason = (v.reason || '').trim();
                if (!reason) {
                    message.error('Leave reason is required');
                    return;
                }
                await setStatus({
                    personId: prompt.personId,
                    dateKey: prompt.dateKey,
                    status: 'LV',
                    reason,
                });
                setPromptOpen(false);
                return;
            }

            await setStatus({
                personId: prompt.personId,
                dateKey: prompt.dateKey,
                status: 'P',
            });

            setPromptOpen(false);
        } catch (e) {
            if (e?.message === 'Validation failed') return;
            onError?.(e);
        }
    };

    if (loading) {
        return (
            <TableShell style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
            </TableShell>
        );
    }

    if (!safeRows.length) {
        return (
            <TableShell style={{ padding: 24 }}>
                <Empty description="No members found" />
            </TableShell>
        );
    }

    return (
        <TableShell>
            <ScrollX>
                <HeaderGrid $days={daysInMonth} $minWidth={minWidth}>
                    <StickyHeaderMember>
                        <Text strong style={{ fontSize: 12, letterSpacing: '0.5px', color: '#64748b', textTransform: 'uppercase' }}>
                            MEMBER NAME
                        </Text>
                    </StickyHeaderMember>

                    {dayCols.map((d) => (
                        <HeaderCell key={d.dateKey} $isToday={d.isToday}>
                            <DayHeader>
                                <DayNum $isToday={d.isToday}>{d.labelNum}</DayNum>
                                <DayName $isWeekend={d.isWeekend} $isToday={d.isToday}>
                                    {d.labelDow.toUpperCase()}
                                </DayName>
                            </DayHeader>
                        </HeaderCell>
                    ))}

                    <StickyHeaderTotal>
                        <Text strong style={{ fontSize: 12, letterSpacing: '0.5px', color: '#64748b', textTransform: 'uppercase' }}>
                            TOTAL
                        </Text>
                    </StickyHeaderTotal>
                </HeaderGrid>

                {safeRows.map((r, idx) => {
                    const personId = getRowId?.(r);
                    const name = getRowName?.(r) || '—';
                    const email = getRowEmail?.(r) || r?.email || r?.userEmail || '';

                    // Calculate total Present count for this member
                    let totalPresent = 0;
                    dayCols.forEach((d) => {
                        const key = `${d.dateKey}_${personId}`;
                        const entry = entryMap.get(key);
                        if (entry?.status === 'P') {
                            totalPresent += 1;
                        }
                    });

                    return (
                        <RowGrid key={personId || idx} $days={daysInMonth} $minWidth={minWidth}>
                            <StickyMemberCell>
                                <MemberWrap>
                                    <AvatarCircle $bg={getAvatarBg(idx, name)}>
                                        {getInitial(name)}
                                    </AvatarCircle>
                                    <MemberMeta>
                                        <MemberName title={name}>{name}</MemberName>
                                        <MemberEmail title={email}>{email || '—'}</MemberEmail>
                                    </MemberMeta>
                                </MemberWrap>
                            </StickyMemberCell>

                            {dayCols.map((d) => {
                                const key = `${d.dateKey}_${personId}`;
                                const entry = entryMap.get(key);
                                const entryStatus = entry?.status || null;
                                const derived = calcDefaultStatus({
                                    date: d.date,
                                    now,
                                    hasEntry: !!entryStatus,
                                });

                                const off = isOffDay({ attendanceConfig, date: d.date });
                                const disabled = derived.disabled || !personId;
                                
                                // Status priority: explicit entry status > Holiday (H) > Default Absent (A) > Future (--)
                                const shown = entryStatus === 'EV' ? 'E' : (entryStatus || (off ? 'H' : derived.value || '--'));
                                const isPresent = shown === 'P';
                                const busy = savingKey === key;

                                if (disabled) {
                                    return (
                                        <Cell key={d.dateKey} $isPresent={false} $isToday={d.isToday}>
                                            <MarkBtn type="button" $status={shown} disabled>
                                                {shown}
                                            </MarkBtn>
                                        </Cell>
                                    );
                                }

                                const contextMenuItems = [
                                    {
                                        key: 'toggle_p',
                                        label: 'Mark Present (P)',
                                        onClick: () => setStatus({ personId, dateKey: d.dateKey, status: 'P' }),
                                    },
                                    {
                                        key: 'toggle_a',
                                        label: 'Mark Absent (A)',
                                        onClick: () => setStatus({ personId, dateKey: d.dateKey, status: 'A' }),
                                    },
                                    {
                                        key: 'custom_details',
                                        label: 'More Options (Leave / Time / Event)',
                                        onClick: () => openPrompt({ personId, dateKey: d.dateKey, date: d.date }),
                                    },
                                    { type: 'divider' },
                                    {
                                        key: 'view',
                                        label: 'View Cell Details',
                                        onClick: () => {
                                            setContext({
                                                personId,
                                                name: nameById.get(personId) || name || '—',
                                                dateKey: d.dateKey,
                                                date: d.date,
                                                monthKey,
                                                scope,
                                            });
                                            setViewOpen(true);
                                        },
                                    },
                                    {
                                        key: 'history',
                                        label: 'Member Month Summary',
                                        onClick: () => {
                                            setContext({
                                                personId,
                                                name: nameById.get(personId) || name || '—',
                                                dateKey: d.dateKey,
                                                date: d.date,
                                                monthKey,
                                                scope,
                                            });
                                            setHistoryOpen(true);
                                        },
                                    },
                                ];

                                return (
                                    <Cell key={d.dateKey} $isPresent={isPresent} $isToday={d.isToday}>
                                        <Dropdown trigger={['contextMenu']} menu={{ items: contextMenuItems }}>
                                            <span style={{ width: '100%', height: '100%', display: 'flex' }}>
                                                <MarkBtn
                                                    type="button"
                                                    $status={shown}
                                                    disabled={busy}
                                                    onClick={() => handleCellClick(personId, d.dateKey, d.date, shown)}
                                                    title={`Click to toggle P/A. Right-click for options. (${dayjs(d.date).format('D MMM YYYY')})`}
                                                >
                                                    {busy ? '…' : shown}
                                                </MarkBtn>
                                            </span>
                                        </Dropdown>
                                    </Cell>
                                );
                            })}

                            <StickyTotalCell>
                                {totalPresent}
                            </StickyTotalCell>
                        </RowGrid>
                    );
                })}
            </ScrollX>

            <Modal
                title="Attendance details"
                open={viewOpen}
                onCancel={() => setViewOpen(false)}
                footer={null}
                destroyOnClose
            >
                {(() => {
                    const pid = context?.personId;
                    const dateKey = context?.dateKey;
                    if (!pid || !dateKey) return null;
                    const entry = entryMap.get(`${dateKey}_${pid}`) || null;
                    const off = context?.date ? isOffDay({ attendanceConfig, date: context.date }) : false;
                    const derived = context?.date
                        ? calcDefaultStatus({ date: context.date, now, hasEntry: !!entry?.status })
                        : { value: '--', disabled: false };
                    const effectiveStatus = entry?.status || (off ? 'H' : derived.value || '--');
                    const startHHmm = context?.date ? startTimeForDate({ attendanceConfig, date: context.date }) : null;
                    const start12 = to12h(startHHmm);

                    const statusTagColor =
                        effectiveStatus === 'P' ? 'green' :
                            effectiveStatus === 'A' ? 'red' :
                                effectiveStatus === 'LV' ? 'orange' :
                                    effectiveStatus === 'EV' ? 'blue' :
                                        effectiveStatus === 'H' ? 'geekblue' :
                                            'default';

                    return (
                        <>
                            <Descriptions size="small" column={1} bordered style={{ marginTop: 12 }}>
                                <Descriptions.Item label="Member">{context?.name || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Date">
                                    {context?.date ? dayjs(context.date).format(dateFormat) : dateKey}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={statusTagColor}>{statusLabel(effectiveStatus)}</Tag>
                                </Descriptions.Item>
                                {start12 ? (
                                    <Descriptions.Item label="School start time">{start12}</Descriptions.Item>
                                ) : null}
                                {entry?.status === 'P' && entry?.checkInTime ? (
                                    <Descriptions.Item label="Check-in time">{entry.checkInTime}</Descriptions.Item>
                                ) : null}
                                {(entry?.status === 'LV' || entry?.status === 'EV') ? (
                                    <Descriptions.Item label={entry?.status === 'LV' ? 'Leave reason' : 'Event name'}>
                                        {entry?.reason || '—'}
                                    </Descriptions.Item>
                                ) : null}
                                {entry?.updatedAt ? (
                                    <Descriptions.Item label="Last updated">
                                        {typeof entry.updatedAt?.toDate === 'function'
                                            ? dayjs(entry.updatedAt.toDate()).format(dateTimeFormat)
                                            : '—'}
                                    </Descriptions.Item>
                                ) : null}
                            </Descriptions>
                            <Divider style={{ margin: '12px 0' }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Tip: Click cell to toggle Present/Absent instantly. Right-click opens options.
                            </Text>
                        </>
                    );
                })()}
            </Modal>

            <Modal
                title="Attendance History (Month)"
                open={historyOpen}
                onCancel={() => setHistoryOpen(false)}
                footer={null}
                destroyOnClose
                width={640}
            >
                {!context?.personId ? null : (
                    <>
                        <Descriptions size="small" column={1} bordered style={{ marginTop: 12 }}>
                            <Descriptions.Item label="Member">{context?.name || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Month">{monthKey}</Descriptions.Item>
                        </Descriptions>
                        <Divider style={{ margin: '16px 0' }} />
                        <Text type="secondary">
                            Use the monthly matrix to view and manage daily entries.
                        </Text>
                    </>
                )}
            </Modal>

            <Modal
                title={prompt?.mode === 'today' ? 'Mark attendance (Today)' : 'Update attendance'}
                open={promptOpen}
                onCancel={() => setPromptOpen(false)}
                onOk={() => void submitPrompt()}
                okText="Save"
                destroyOnClose
            >
                <Form form={promptForm} layout="vertical" style={{ marginTop: 12 }}>
                    {prompt?.start12 ? (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            School start time: {prompt.start12}
                        </Text>
                    ) : null}

                    {prompt?.holiday ? (
                        <>
                            <Form.Item name="holidayAction" label="Holiday day action" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        { label: 'Present (P)', value: 'P' },
                                        { label: 'Event (E)', value: 'EV' },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(p, n) => p.holidayAction !== n.holidayAction}
                            >
                                {({ getFieldValue }) => {
                                    const a = getFieldValue('holidayAction');
                                    if (a !== 'EV') return null;
                                    return (
                                        <Form.Item name="eventName" label="Event name" rules={[{ required: true }]}>
                                            <TextArea rows={2} placeholder="e.g. Sports day / Exam / Training" />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </>
                    ) : prompt?.mode === 'today' ? (
                        <>
                            <Form.Item name="todayStatus" label="Status" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        { label: 'Present (P)', value: 'P' },
                                        { label: 'Leave (LV)', value: 'LV' },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(p, n) => p.todayStatus !== n.todayStatus}
                            >
                                {({ getFieldValue }) => {
                                    const s = getFieldValue('todayStatus');
                                    if (s === 'LV') {
                                        return (
                                            <Form.Item name="reason" label="Leave reason (required)" rules={[{ required: true }]}>
                                                <TextArea rows={3} placeholder="Enter reason" />
                                            </Form.Item>
                                        );
                                    }
                                    return (
                                        <Form.Item name="todayCheckIn" label="Check-in time" rules={[{ required: true }]}>
                                            <TimePicker use12Hours format="hh:mm A" minuteStep={5} style={{ width: '100%' }} />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Form.Item name="pastStatus" label="Status" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        { label: 'Present (P)', value: 'P' },
                                        { label: 'Leave (LV)', value: 'LV' },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(p, n) => p.pastStatus !== n.pastStatus}
                            >
                                {({ getFieldValue }) => {
                                    const s = getFieldValue('pastStatus');
                                    if (s !== 'LV') return null;
                                    return (
                                        <Form.Item name="reason" label="Leave reason (required)" rules={[{ required: true }]}>
                                            <TextArea rows={3} placeholder="Enter reason" />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </TableShell>
    );
}
